import { useState } from 'react';
import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { isEmpty } from 'lodash';
import cs from 'classnames';
import { Row, Col } from 'antd';
import { useTranslation, Trans } from 'react-i18next';
import { CurrentUser, StoreState } from '@root/types';
import fetchCurrentUserAction, {
  updateCurrentUser,
  accountSettingsPageLoad,
} from '@store/actions/currentUser';
import { Link, useParams } from 'react-router-dom';
import routes from '@root/routes';
import OnlyInMobile from '@components/shared/OnlyInMobile';
import OnlyInDesktopTablet from '@components/shared/OnlyInDesktopTablet';
import Layout from '../Layout';
import SettingsDisplay from './SettingsDisplay/SettingsDisplay';
import SettingsEdit from './SettingsEdit/SettingsEdit';
import s from './AccountSettings.module.less';
import NotFound from '../404/NotFound';

const ValidTabs = ['professional_info', 'tourbook_contact_info'] as const;
export type AccountSettingsTab = typeof ValidTabs[number];
const mapStateToProps = ({ currentUser }: StoreState) => ({ currentUser });
const mapDispatchToProps = dispatch => ({
  updateUser: ({ user, onSuccess, onError, analyticsInfo }) =>
    dispatch(updateCurrentUser({ user, onSuccess, onError, ...analyticsInfo })),
  fetchCurrentUser: () => dispatch(fetchCurrentUserAction()),
  loadPage: (tab: NonNullable<AccountSettingsTab>) => dispatch(accountSettingsPageLoad(tab)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export const RawAccountSettings = ({
  currentUser = null,
  updateUser,
  fetchCurrentUser,
  loadPage,
}: ReduxProps) => {
  const { t } = useTranslation('accountSettings');
  const defaultEditableState = {
    professionalInfo: false,
    tourbookContactInfo: false,
  };
  const [editableState, setEditableState] = useState(defaultEditableState);
  const { accountSettingsTab } =
    useParams<{ accountSettingsTab: AccountSettingsTab }>() || 'professional_info';

  React.useEffect(() => {
    loadPage(accountSettingsTab);
  }, [accountSettingsTab]);

  if (isEmpty(currentUser)) {
    return null;
  }

  const goBack = (tab: AccountSettingsTab) => () => {
    setEditableState(prevState => {
      const newState = { ...prevState };
      newState[tab] = false;
      return newState;
    });
  };

  if (!ValidTabs.includes(accountSettingsTab)) {
    return <NotFound />;
  }

  return (
    <Layout activePage="userAccountSettings">
      <div className={s.accountSettings}>
        <div className={s.questionsMessage}>
          {/* eslint-disable react/jsx-no-literals */}
          <Trans i18nKey="questionsMessage" ns="accountSettings">
            Questions or issues with your account? Contact VTS Marketplace support at
            <a href="mailto:support@vts.com">support@vts.com</a>
            <a href="https://help.vts.com/hc/en-us">help center</a>
          </Trans>
          {/* eslint-enable react/jsx-no-literals */}
        </div>
        <h1 className={s.accountSettingsTitle}>{t('yourAccountTitle')}</h1>
        <Row className={s.mainContent}>
          <Col className={s.menuColumn} sm={0} md={6} lg={6}>
            <div className={s.menu}>
              <ul>
                <Link
                  to={routes.users.edit('professional_info')}
                  className={cs(s.menuItem, {
                    [s.selected]: accountSettingsTab === 'professional_info',
                  })}
                >
                  <li>{t`professionalInfoTitle`}</li>
                </Link>
                <Link
                  to={routes.users.edit('tourbook_contact_info')}
                  className={cs(s.menuItem, {
                    [s.selected]: accountSettingsTab === 'tourbook_contact_info',
                  })}
                >
                  <li>{t`tourbookInfoTitle`}</li>
                </Link>
              </ul>
            </div>
          </Col>
          <Col sm={24} md={18} lg={18}>
            <Row>
              <Col sm={24} md={24} lg={16}>
                <OnlyInDesktopTablet>
                  {editableState[accountSettingsTab] ? (
                    <SettingsEdit
                      currentUser={currentUser as CurrentUser}
                      currentTab={accountSettingsTab}
                      goBack={goBack}
                      updateUser={updateUser}
                      fetchCurrentUser={fetchCurrentUser}
                    />
                  ) : (
                    <SettingsDisplay
                      currentUser={currentUser}
                      currentTab={accountSettingsTab}
                      setEditableState={setEditableState}
                    />
                  )}
                </OnlyInDesktopTablet>
                <OnlyInMobile>
                  {editableState.professionalInfo ? (
                    <SettingsEdit
                      currentUser={currentUser as CurrentUser}
                      currentTab="professional_info"
                      goBack={goBack}
                      updateUser={updateUser}
                      fetchCurrentUser={fetchCurrentUser}
                    />
                  ) : (
                    <SettingsDisplay
                      currentUser={currentUser}
                      currentTab="professional_info"
                      setEditableState={setEditableState}
                    />
                  )}
                  {editableState.tourbookContactInfo ? (
                    <SettingsEdit
                      currentUser={currentUser as CurrentUser}
                      currentTab="tourbook_contact_info"
                      goBack={goBack}
                      updateUser={updateUser}
                      fetchCurrentUser={fetchCurrentUser}
                    />
                  ) : (
                    <SettingsDisplay
                      currentUser={currentUser}
                      currentTab="tourbook_contact_info"
                      setEditableState={setEditableState}
                    />
                  )}
                </OnlyInMobile>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </Layout>
  );
};

export default connector(RawAccountSettings) as React.FC;
